import { Tabs, TabsContent, TabsList, TabsTrigger } from '@design-system';
import {
  useTabConfiguration,
  type PaymentOption,
  type ShopInsuranceMode,
} from '@/features/shop-insurance';
import type { ReactNode } from 'react';

interface ShopInsuranceTabsWrapperProps {
  hasPartnerWithDemographics: boolean;
  isMarriedOrPartnered: boolean;
  paymentOption: PaymentOption;
  selectedTab: ShopInsuranceTab;
  selectedBuyerOption: {
    key: '1-buyer' | '2-buyer';
    label: string;
    maritalClass: string;
  };
  tabContent: ReactNode;
  handleTabChangeWithFetch: (tabValue: string) => void;
  handleBuyerOptionChange: (buyerKey: '1-buyer' | '2-buyer') => void;
  featureFlags?: FeatureFlags;
  mode?: ShopInsuranceMode;
}

const scrollableContentClass = 'pb-0';

export function ShopInsuranceTabsWrapper({
  hasPartnerWithDemographics,
  isMarriedOrPartnered,
  paymentOption,
  selectedTab,
  selectedBuyerOption,
  tabContent,
  handleTabChangeWithFetch,
  handleBuyerOptionChange,
  featureFlags,
  mode,
}: ShopInsuranceTabsWrapperProps) {
  const { policyTabs, buyerTabs } = useTabConfiguration({
    hasPartnerWithDemographics,
    featureFlags,
  });

  const isBenefitDesign = mode === 'benefit-design';

  if (
    isMarriedOrPartnered &&
    (isBenefitDesign || !['Monthly', 'Annually'].includes(paymentOption))
  ) {
    if (policyTabs.length === 1) {
      return <div className={scrollableContentClass}>{tabContent}</div>;
    }

    return (
      <Tabs
        value={selectedTab}
        defaultValue={hasPartnerWithDemographics ? 'joint' : 'single'}
        onValueChange={handleTabChangeWithFetch}
      >
        <TabsList className="mb-4">
          {policyTabs.map(tab => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              data-testid={tab.dataTestId}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {policyTabs.map(tab => (
          <TabsContent
            key={tab.value}
            value={tab.value}
            className={scrollableContentClass}
          >
            {tabContent}
          </TabsContent>
        ))}
      </Tabs>
    );
  }

  if (isMarriedOrPartnered && ['Monthly', 'Annually'].includes(paymentOption)) {
    return (
      <Tabs
        value={selectedBuyerOption.key}
        defaultValue="1-buyer"
        onValueChange={value => handleBuyerOptionChange(value as '1-buyer' | '2-buyer')}
      >
        <TabsList className="mb-4">
          {buyerTabs.map(tab => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {buyerTabs.map(tab => (
          <TabsContent
            key={tab.value}
            value={tab.value}
            className={scrollableContentClass}
          >
            {tabContent}
          </TabsContent>
        ))}
      </Tabs>
    );
  }

  return <div className={scrollableContentClass}>{tabContent}</div>;
}
